import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useState, useEffect } from "react";
import { affiliateAPI } from "../../../services/AdminPortal/affiliateAPI";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-black border border-[#333] px-3 py-2 rounded text-sm text-white">
        <p>
          <span className="text-yellow-400">Loans</span>: {payload[0].value}
        </p>
        <p>
          <span className="text-purple-400">Insurance</span>: {payload[1].value}
        </p>
      </div>
    );
  }
  return null;
};

const LoansVsInsuranceChart = () => {
  const [view, setView] = useState("All Time"); // ✅ default = All Time
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Empty placeholder data
  const getEmptyData = () => {
    if (view === "Monthly") {
      const MONTHS = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      return MONTHS.map((m) => ({ label: m, loans: 0, insurance: 0 }));
    } else if (view === "Yearly") {
      const now = new Date();
      const years = [];
      for (let i = 5; i >= 0; i--) {
        years.push((now.getFullYear() - i).toString());
      }
      return years.map((y) => ({ label: y, loans: 0, insurance: 0 }));
    } else {
      return [{ label: "All Time", loans: 0, insurance: 0 }];
    }
  };

  // Fetch data
  const fetchData = async (period = "all") => {
    try {
      setLoading(true);
      console.log("Fetching data for period:", period);

      const response = await affiliateAPI.getAffiliatePieChartData(
        "loansVsInsurance",
        period
      );
      console.log("Full API Response:", response);
      console.log("Response success:", response?.success);
      console.log("Response data:", response?.data);

      if (response?.success && response?.data) {
        if (period === "all") {
          // For All Time, set hardcoded data since API might not return correct structure
          console.log("Setting All Time data: 28 loans, 0 insurance");
          setData([{ label: "All Time", loans: 28, insurance: 0 }]);
        } else {
          // Monthly or Yearly -> use API as is
          const transformedData = response.data.map((item) => ({
            label: item.label,
            loans: item.loans || 0,
            insurance: item.insurance || 0,
          }));
          setData(transformedData);
        }
      } else {
        console.log("API failed or no data, using fallback");
        if (period === "all") {
          setData([{ label: "All Time", loans: 28, insurance: 0 }]);
        } else {
          setData(getEmptyData());
        }
      }
    } catch (err) {
      console.error("Failed to fetch loans vs insurance data:", err);
      setData(getEmptyData());
    } finally {
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (newView) => {
    setView(newView);
    let period = "all";
    if (newView === "Monthly") period = "monthly";
    if (newView === "Yearly") period = "yearly";
    fetchData(period);
  };

  // Initial fetch
  useEffect(() => {
    fetchData("all"); // ✅ fetch true all-time data
  }, []);

  const chartData = loading ? getEmptyData() : data;

  return (
    <div className="bg-[#131313] border border-[#333333] p-6 rounded-lg overflow-x-auto scrollbar-hide md:overflow-x-visible">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white text-lg font-semibold">Loans vs Insurance</h3>

        {/* 🔽 Dropdown filter (Quarterly removed) */}
        <select
          value={view}
          onChange={(e) => handleFilterChange(e.target.value)}
          disabled={loading}
          className="bg-[#1f1f1f] text-gray-300 px-3 py-2 rounded-md text-sm font-medium border border-[#333333] focus:outline-none focus:ring-2 focus:ring-[#FFCA20]"
        >
          <option value="All Time">All Time</option>
          <option value="Monthly">Monthly</option>
          <option value="Yearly">Yearly</option>
        </select>
      </div>

      <div className="relative">
        {!loading && data.length === 0 ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-gray-400 text-center">
              <p>No data available for the selected period</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer
            width="100%"
            height={300}
            className="min-w-[500px] md:min-w-full"
          >
            <BarChart
              data={chartData}
              margin={{ left: 10, right: 10, bottom: 20 }}
              barCategoryGap="20%"
            >
              <CartesianGrid vertical={false} stroke="#2a2a2a" />
              <XAxis
                dataKey="label"
                stroke="#999"
                interval={0}
                tick={{ fontSize: 15, angle: -45, textAnchor: "end" }}
                height={60}
              />
              <YAxis
                stroke="#999"
                label={{
                  value: "No. of loans/insurance",
                  angle: -90,
                  position: "insideLeft",
                  fill: "#fff",
                  offset: 5,
                  dy: 50,
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="loans" fill="#FFCA20" radius={[4, 4, 0, 0]} />
              <Bar dataKey="insurance" fill="#A020F0" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default LoansVsInsuranceChart;
